import React from 'react';

import dayjs from 'dayjs';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { ConvertMembershipFormSchemaValues } from 'src/features/membership/formSchema';
import { Membership } from 'src/features/membership/types';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { useListMembershipTypes } from 'src/features/membershipType/api/useListMembershipTypes';
import { useParking } from 'src/providers/ParkingProvider';
import InputFormField from 'src/registry/components/forms/InputFormField';
import DatePickerWithTime from 'src/registry/ui/DatePickerWithTime';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Separator } from 'src/registry/ui/Separator';
import { generalMessages } from 'src/registry/utils/messages';

type MembershipFormProps = {
    form: UseFormReturn<ConvertMembershipFormSchemaValues>;
    onSubmit: (values: ConvertMembershipFormSchemaValues) => void;
    selectedMembership?: Membership;
};
function ConvertMembershipForm({ onSubmit, form, selectedMembership }: MembershipFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: membershipTypes } = useListMembershipTypes({
        is_active: true,
        parkingId,
    });

    const validFrom = form.watch('valid_from');
    const selectedMembershipTypeId = form.watch('membership_type');

    const selectedMembershipType = React.useMemo(() => {
        return membershipTypes && membershipTypes.results.find((type) => type.id === selectedMembershipTypeId);
    }, [selectedMembershipTypeId, membershipTypes]);

    React.useEffect(() => {
        if (!selectedMembershipType) return;

        form.setValue('max_allowed_spaces_count', selectedMembershipType.max_allowed_spaces_count);

        const { duration_unit, duration } = selectedMembershipType;

        if (!duration_unit || !duration || !validFrom) return;

        const newValidTo = dayjs(validFrom).add(duration, duration_unit).toISOString();
        form.setValue('valid_to', newValidTo);
    }, [form, selectedMembershipType, validFrom]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="membership_type"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...membershipMessages.template} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(membershipMessages.template)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {membershipTypes?.results?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {
                                                    option.translations.find(
                                                        (translation) => translation.language === locale,
                                                    )?.name
                                                }
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <Separator />

            <FormItem variant="dialog">
                <FormLabel variant="dialog">{formatMessage(generalMessages.name)}</FormLabel>
                <FormControlStack className="cursor-not-allowed">
                    <FormControl>
                        <Input disabled={true} value={selectedMembership?.name} />
                    </FormControl>
                </FormControlStack>
            </FormItem>

            <FormItem variant="dialog">
                <FormLabel variant="dialog">{formatMessage(membershipMessages.entity)}</FormLabel>
                <FormControl>
                    <Select disabled>
                        <SelectTrigger className="w-full">
                            {selectedMembership?.owner?.name ?? formatMessage(membershipMessages.noEntitySelected)}
                        </SelectTrigger>
                    </Select>
                </FormControl>
            </FormItem>

            <Separator />

            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(membershipMessages.validFrom)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabledDateRange={{ before: dayjs().startOf('day').toDate() }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(membershipMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabled={
                                        !!(selectedMembershipType?.duration && selectedMembershipType?.duration_unit)
                                    }
                                    disabledDateRange={{ before: dayjs().startOf('day').toDate() }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            {selectedMembershipType?.max_allowed_spaces_count && (
                <InputFormField<ConvertMembershipFormSchemaValues>
                    form={form}
                    name="max_allowed_spaces_count"
                    label={formatMessage(membershipMessages.maxAllowedSpaces)}
                    type="number"
                    disabled={true}
                    required={false}
                    min={0}
                />
            )}
        </Form>
    );
}

export default ConvertMembershipForm;
